import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { CaretDownFill, Clipboard, Calendar3, CaretUpFill, ArrowCounterclockwise, ChevronDown, ThreeDots, Download } from 'react-bootstrap-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getUrlDateRange } from '../../utils/dashboardDates';
import { isStepFailure } from '../../utils/dataLayerGuard';
import 'react-datepicker/dist/react-datepicker.css';
import { format, set } from 'date-fns';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import localStorageService from '../../services/localStorage.service';
import DashboardService from '../../services/dashboard.service';
import { AuthContext } from '../../services/auth.service';

import PageHeader from '../../components/elements/PageHeader';
import LoadingScreen from '../../components/elements/LoadingScreen';
import Tabs from '../../components/elements/Tabs';

import { Layout, Row, Col, Flex, Space, DatePicker, Select, Dropdown, Switch, Table as AntdTable } from 'antd';

import "./styles.scss";
const { Content } = Layout;
const { RangePicker } = DatePicker;


export const getDateRangeCode = (startDateStr, endDateStr) => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    // Calculate the difference in milliseconds
    const differenceMs = Math.abs(endDate - startDate);

    // Number of milliseconds in a day
    const oneDayMs = 1000 * 60 * 60 * 24;

    // Number of milliseconds in an hour
    const oneHourMs = 1000 * 60 * 60;

    // Get the current date and time
    const now = new Date();

    // Calculate the difference between now and the end date
    const differenceFromNowMs = Math.abs(now - endDate);

    let result;

    switch (differenceMs) {
        case oneDayMs:
            if (differenceFromNowMs <= oneDayMs) {
                result = "last_24_hours";
            } else if (differenceMs === oneDayMs) {
                result = "one_day";
            }
            break;
        case oneHourMs:
            result = "last_hour";
            break;
        case 0:
            result = "one_day";
            break;
        default:
            result = null;
    }

    return result;
}


// Admin-only preference: admins see internal step-diagnostic rows by default,
// but can hide them (e.g. during a demo) to surface only real errors.
const STEP_DIAGNOSTICS_STORAGE_KEY = 'dlg_show_step_diagnostics';

const ERROR_CATEGORY = {
    COOKIE: 'cookie',
    FLOW_MONITOR: 'flow_monitor',
    DATALAYER: 'datalayer',
};

const FLOW_MONITOR_ERROR_TYPES = new Set([
    'missing_network_data',
    'missing_network_event',
    'metadata_assertion_failed',
    'network_not_contains',
    'unexpected_network_events',
]);

const FLOW_MONITOR_TYPE_LABEL = {
    missing_network_data: 'No network request captured',
    missing_network_event: 'No matching network event',
    metadata_assertion_failed: 'Network metadata mismatch',
    network_not_contains: 'Forbidden content in request',
    unexpected_network_events: 'Unexpected network requests fired',
};

const getErrorCategory = (row) => {
    if (!row) return ERROR_CATEGORY.DATALAYER;
    if (row.event_name === 'cookie_check' || row.page_type === 'cookie_check') {
        return ERROR_CATEGORY.COOKIE;
    }
    if (FLOW_MONITOR_ERROR_TYPES.has(row.error_type)) {
        return ERROR_CATEGORY.FLOW_MONITOR;
    }
    return ERROR_CATEGORY.DATALAYER;
};

const safeParseJSON = (value) => {
    if (value == null) return null;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

// Normalizes the per-parameter error data into an array, supporting both the
// backend-built `parameters` field and the flattened single-parameter row that
// the dashboard query currently returns for cookie / flow-monitor errors.
const getNormalizedParameters = (row) => {
    if (Array.isArray(row?.parameters) && row.parameters.length > 0) {
        return row.parameters;
    }
    const hasFlat =
        row?.parameter_name || row?.error || row?.description ||
        row?.expected_value || row?.actual_value ||
        row?.expected_type || row?.actual_type;
    if (!hasFlat) return [];
    return [{
        parameter_name: row.parameter_name,
        error: row.error,
        description: row.description,
        expected_value: row.expected_value,
        actual_value: row.actual_value,
        expected_type: row.expected_type,
        actual_type: row.actual_type,
    }];
};

const formatCookieExpected = (value) => {
    if (value === null || value === undefined || value === '') return '(none)';
    return String(value);
};

const formatCookieActual = (value) => {
    if (value === null || value === undefined || value === '') return '(cookie not set)';
    return String(value);
};

const truncateText = (str, max = 90) => {
    const s = String(str || '');
    return s.length > max ? `${s.slice(0, max)}...` : s;
};

// Cap a string for display in a <textarea> (Copy/Download still use the full object).
const MAX_DATALAYER_DISPLAY_CHARS = 10000;
const truncateForDisplay = (value, max = MAX_DATALAYER_DISPLAY_CHARS) => {
    const s = String(value ?? '');
    if (s.length <= max) return s;
    return `${s.slice(0, max)}\n\n… [truncated for display — showing the first ${max.toLocaleString()} of ${s.length.toLocaleString()} characters. Use Download to get the full snapshot.]`;
};

const UnexpectedNetworkEventsList = ({ items }) => {
    const [expandedIdx, setExpandedIdx] = useState(null);
    return (
        <ul className="unexpected-network-list">
            {items.map((item, idx) => {
                const isRequestSummary = item?.type === 'network_request';
                const method = item?.method || (isRequestSummary ? 'GET' : 'POST');
                const url = item?.url || '';
                const isExpanded = expandedIdx === idx;
                return (
                    <li
                        key={`net-${idx}`}
                        className="unexpected-network-item"
                    >
                        <div
                            className="unexpected-network-summary"
                            role="button"
                            onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                        >
                            <span className="unexpected-network-method">{method}</span>
                            <span className="unexpected-network-url" title={url}>
                                {isExpanded ? url : truncateText(url)}
                            </span>
                            <span className="unexpected-network-toggle">
                                {isExpanded ? <CaretUpFill size={12} /> : <CaretDownFill size={12} />}
                            </span>
                        </div>
                        {isExpanded && (
                            <pre className="unexpected-network-detail">
                                {JSON.stringify(item, null, 2)}
                            </pre>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

export const DataLayerGuardDash = () => {

    const navigate = useNavigate();

    const { companyId, companyInfo, adminUser } = useContext(AuthContext);
    const [companyID, setCompanyID] = useState(companyId);
    const [datasetFound, setDatasetFound] = useState(true);
    const [filterButtons, setfilterButtons] = useState(true);
    const [inititalLoad, setInititalLoad] = useState(true);
    const [clientTimeStamp, setClientTimeStamp] = useState("Europe/Amsterdam");

    const [loadingActive, setLoadingActive] = useState(true);
    const [activeTab, setActiveTab] = useState("errors");

    // Set the loading status for the loading spinner
    const [updateLoadFlag, setUpdateLoadFlag] = useState(false);

    const [errorsList, setErrorsList] = useState([]);
    const [initialErrorsList, setInitialErrorsList] = useState([]);
    const [errorToReview, setErrorToReview] = useState({});
    const [errorToReviewKey, setErrorToReviewKey] = useState(null);
    // datalayer_hash -> full blob, to restore rows the backend de-duplicated to "".
    const datalayerBlobMapRef = useRef({});
    const [errorExpanded, setErrorExpanded] = useState(false);
    const [issueContent, setIssueContent] = useState(<></>);
    const [eventNameFilter, setEventNameFilter] = useState("");

    const [snapshotsList, setSnapshotsList] = useState([]);
    const [initialSnapshotsList, setInitialSnapshotsList] = useState([]);
    const [snapshotToReview, setSnapshotToReview] = useState({});
    const [snapshotToReviewKey, setSnapshotToReviewKey] = useState(null);
    const [snapshotExpanded, setSnapshotExpanded] = useState(false);

    //Dashboard Data
    const [domainsList, setDomainsList] = useState([]);

    // Filters
    const [filtersDisabled, setFiltersDisabled] = useState(true);

    // Admin-only toggle to show/hide internal step-diagnostic rows (default ON).
    const [showStepDiagnostics, setShowStepDiagnostics] = useState(() => {
        const stored = localStorage.getItem(STEP_DIAGNOSTICS_STORAGE_KEY);
        return stored === null ? true : stored === 'true';
    });
    const handleToggleStepDiagnostics = (checked) => {
        setShowStepDiagnostics(checked);
        localStorage.setItem(STEP_DIAGNOSTICS_STORAGE_KEY, String(checked));
    };

    // List of event names and hosts
    const [eventNameList, setEventNameList] = useState([]);

    // Filters Data
    const [chosenEventName, setChosenEventName] = useState([]);
    const [chosenDomain, setChosenDomain] = useState([]);

    // current values of the filters
    const [currentChosenEventName, setCurrentChosenEventName] = useState([]);
    const [currentChosenDomain, setCurrentChosenDomain] = useState([]);

    // Opens on the homepage's period from "View more"; a single day is "one_day", like a day picked in the picker
    const [searchParams] = useSearchParams();
    const [urlDateRange] = useState(() => getUrlDateRange(searchParams, 'one_day'));
    const [startDate, setStartDate] = useState(urlDateRange?.start || dayjs().add(-7, 'd').set('hour', 0).set('minute', 0).set('second', 0));
    const [endDate, setEndDate] = useState(urlDateRange?.end || dayjs().set('hour', 23).set('minute', 59).set('second', 59));

    const timestampFormat = "YYYY-MM-DD HH:mm:ss";
    const timestampFormatInput = "YYYY-MM-DD";

    const [differenceOneDayOrOneHour, setDifferenceOneDayOrOneHour] = useState(urlDateRange?.window ?? null);
    const [logoBase64, setLogoBase64] = useState(null);
    const [logoAspectRatio, setLogoAspectRatio] = useState(5.5);
    const [isLargeViewport, setIsLargeViewport] = useState(
        typeof window !== 'undefined' && window.innerWidth >= 1600,
    );

    const rangePresets = [
        {
            label: 'Last 1 Hour',
            value: [dayjs().subtract(1, 'hour'), dayjs()],
        },
        {
            label: 'Last 24 Hours',
            value: [dayjs().subtract(24, 'hour'), dayjs()],
        },
        {
            label: 'Last 7 Days',
            value: [
                dayjs().add(-7, 'd').set('hour', 0).set('minute', 0).set('second', 0),
                dayjs().set('hour', 23).set('minute', 59).set('second', 59)
            ],
        },
        {
            label: 'Last 14 Days',
            value: [
                dayjs().add(-14, 'd').set('hour', 0).set('minute', 0).set('second', 0),
                dayjs().set('hour', 23).set('minute', 59).set('second', 59)
            ],
        },
        {
            label: 'Last 30 Days',
            value: [
                dayjs().add(-30, 'd').set('hour', 0).set('minute', 0).set('second', 0),
                dayjs().set('hour', 23).set('minute', 59).set('second', 59)
            ],
        },
        {
            label: 'Last 90 Days',
            value: [
                dayjs().add(-90, 'd').set('hour', 0).set('minute', 0).set('second', 0),
                dayjs().set('hour', 23).set('minute', 59).set('second', 59)
            ],
        },
    ];

    const timeAgo = (timestamp) => {
        const currentDate = new Date();
        const pastDate = new Date(timestamp);

        const amsterdamTime = currentDate.toLocaleString('en-US', { timeZone: clientTimeStamp });
        const amsterdamDate = new Date(amsterdamTime);


        const timeDifference = amsterdamDate - pastDate;
        const seconds = Math.floor(timeDifference / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) {
            return `${days} day(s) and ${hours % 24} hour(s) ago`;
        } else if (hours > 0) {
            return `${hours} hour(s) and ${minutes % 60} minute(s) ago`;
        } else if (minutes > 0) {
            return `${minutes} minute(s) ago`;
        } else {
            return `${seconds} second(s) ago`;
        }
    }

    useEffect(() => {
        const handleViewportResize = () => {
            setIsLargeViewport(window.innerWidth >= 1600);
        };

        window.addEventListener('resize', handleViewportResize);
        return () => window.removeEventListener('resize', handleViewportResize);
    }, []);

    const generateTimestamps = (startDate, endDate) => {

        const timestampsList = [];
        const now = new Date();

        const amsterdamTime = now.toLocaleString('en-US', { timeZone: clientTimeStamp });
        const amsterdamHour = new Date(amsterdamTime).getHours();
        const amsterdamDate = new Date(amsterdamTime);

        const lastHourStart = new Date(amsterdamDate.setHours(amsterdamDate.getHours() - 1));
        lastHourStart.setMinutes(amsterdamDate.getMinutes());

        const timeSpan = companyID !== companyId ? null : differenceOneDayOrOneHour;

        const startDateNewDate = new Date(startDate);
        const endDateNewDate = new Date(endDate);

        const formatDate = (date) => {
            // Extract year, month, and day
            let year = date.getFullYear();
            let month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed, so we add 1
            let day = date.getDate().toString().padStart(2, '0');

            // Format the date as YYYY-MM-DD
            let formattedDate = `${year}-${month}-${day}`;
            return formattedDate;
        }

        switch (timeSpan) {
            case "one_day":
            case "last_24_hours":
                for (let i = amsterdamHour; i >= 0; i--) {
                    const hour = `${i.toString()}:00`;
                    const date = formatDate(new Date(endDateNewDate));
                    const fullDateString = `${date} ${hour}`
                    timestampsList.push(fullDateString);
                }

                for (let i = 23; i >= amsterdamHour; i--) {
                    const hour = `${i.toString()}:00`;
                    const date = formatDate(new Date(startDateNewDate));
                    const fullDateString = `${date} ${hour}`
                    timestampsList.push(fullDateString);
                }
                break;

            case "last_hour":
                for (let i = 0; i < 60; i++) {
                    const timeEntry = new Date(lastHourStart.getTime() + i * 60000);
                    const hours = ('0' + timeEntry.getHours()).slice(-2); // Extract hours and pad with leading zero if necessary
                    const minutes = ('0' + timeEntry.getMinutes()).slice(-2); // Extract minutes and pad with leading zero if necessary
                    const formattedTime = `${hours}:${minutes}`;
                    timestampsList.push(formattedTime);
                }
                break;

            case false:
                while (startDateNewDate <= endDateNewDate) {
                    timestampsList.push(formatDate(new Date(startDateNewDate))); // Format as "YYYY-MM-DD"
                    startDateNewDate.setDate(new Date(startDateNewDate.getDate() + 1)); // Increment the date by 1 day
                }
                break;

            default:
        }

        return timestampsList;
    }

    const addMissingTimestamps = (list, aggrParam) => {

        const timestampsList = generateTimestamps(startDate, endDate);

        // Group the results by the combination of tag_id and container_id
        const groupedData = {};
        const errordata = aggrParam === "total_fails_percent" ? true : false;

        list?.forEach((item) => {
            const key = `${item.tag_id}_${item.container_id}`;

            if (!groupedData[key]) {
                groupedData[key] = [];

            }
            groupedData[key].push(item);
        });


        // Add missing timestamps with 0 total_calls for other tags
        for (const key in groupedData) {
            let currentTagData = groupedData[key];

            if (currentTagData) {
                const missingTimestamps = timestampsList.filter((timestamp) => !currentTagData.some((item) => item.timestamp === timestamp));

                if (currentTagData.length > 0) {
                    missingTimestamps.forEach((timestamp) => {
                        currentTagData.push({
                            container_id: currentTagData[0].container_id,
                            tag_id: currentTagData[0].tag_id,
                            tag_name: currentTagData[0].tag_name,
                            timestamp: timestamp,
                            total_calls: 0,
                            data_type: errordata ? currentTagData[0].data_type : null,
                            total_fails: errordata ? 0 : null,
                            total_fails_percent: errordata ? 0 : null,
                        });
                    });
                }
            }
        }

        // extract all objects inside the nested arrays and create a new list
        let arrayOfArrays = Object.values(groupedData);
        const results = arrayOfArrays.reduce((accumulator, currentArray) => {
            return [...accumulator, ...currentArray];
        }, []);


        return results;
    }

    const getTimestamps = (dataArr, aggrParam, setLabelFunc) => {
        let sortedData;

        const addedMissingTimestampsArrData = addMissingTimestamps(dataArr, aggrParam);
        const timeSpan = companyID !== companyId ? null : differenceOneDayOrOneHour;

        switch (timeSpan) {
            case "one_day":
            case "last_24_hours":
                // sort the timestams when they are in the format of "YYYY-MM-DD HH:MM"
                function parseTimestamp(timestamp) {
                    const [date, time] = timestamp.split(" ");
                    const [year, month, day] = date.split("-").map(Number);
                    const [hours, minutes] = time.split(":").map(Number);
                    return new Date(year, month - 1, day, hours, minutes);
                }

                // Sort the array of objects by timestamp property in descending order
                sortedData = addedMissingTimestampsArrData.sort(
                    (a, b) => parseTimestamp(a.timestamp) - parseTimestamp(b.timestamp)
                );
                break;

            case "last_hour":
                // sort the timestams when they are in the format of "HH:MM"
                function timeToMinutes(time) {
                    const [hours, minutes] = time.split(":").map(Number);
                    return hours * 60 + minutes;
                }

                // Sort the array of objects by timestamp property in descending order
                sortedData = addedMissingTimestampsArrData.sort(
                    (a, b) => timeToMinutes(a.timestamp) - timeToMinutes(b.timestamp)
                );
                break;

            default:
                // sort the timestams when they are in the format of regular dates
                sortedData = addedMissingTimestampsArrData.sort(
                    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
                );
        }

        setLabelFunc(retrieveTimestampLabels(sortedData, "timestamp"));
        return sortedData;
    }

    const retrieveTimestampLabels = (arr, labelField) => {
        const sortedTimestamps = [...new Set(arr.map(item => item[labelField]))].sort((a, b) => {
            const timeA = new Date("1970/01/01 " + a);
            const timeB = new Date("1970/01/01 " + b);
            return timeA - timeB;
        });

        return sortedTimestamps;
    };

    const onRangeChange = (dates = [], dateStrings = []) => {
        if (!dates?.[0] || !dates?.[1]) {
            setDefaultDateRange();
            setDifferenceOneDayOrOneHour(null);
            setUpdateLoadFlag(!updateLoadFlag);
            return;
        }

        const dateStart = dates?.length ? dates[0] : "";
        const dateEnd = dates?.length ? dates[1] : "";
        const dateStartStr = dateStrings?.length ? dateStrings[0] : "";
        const dateEndStr = dateStrings?.length ? dateStrings[1] : "";
        
        // Check if same day is selected
        const isSameDay = dateStartStr === dateEndStr && dateStartStr !== "";
        
        // If same day, set start to beginning of day and end to end of day
        let adjustedDateStart = dateStart;
        let adjustedDateEnd = dateEnd;
        
        if (isSameDay) {
            // Only adjust to full day if it's not the "last_hour" preset
            const isLastHour = dateStart && dateEnd && 
                Math.abs(dateEnd.diff(dateStart, 'hour', true)) <= 1 &&
                Math.abs(dateEnd.diff(dayjs(), 'minute')) < 5; // Within 5 minutes of now
                
            if (!isLastHour) {
                adjustedDateStart = dateStart.startOf('day'); // Sets to 00:00:00
                adjustedDateEnd = dateEnd.endOf('day');       // Sets to 23:59:59
            }
        }
        
        const oneDaySpan = getDateRangeCode(dateStartStr, dateEndStr);
        setDifferenceOneDayOrOneHour(oneDaySpan);
        
        // If new date has been selected update the charts
        if (adjustedDateStart !== startDate || adjustedDateEnd !== endDate) {
            if (adjustedDateStart && adjustedDateEnd && companyID === companyId) {
                setUpdateLoadFlag(!updateLoadFlag);
            }
        }
        
        setStartDate(adjustedDateStart);
        setEndDate(adjustedDateEnd);
    };
    const handleFilterChange = (e, updateFunction) => updateFunction(e);

    const handleClear = (updateFunction) => {
        updateFunction([]);
        handleLoseFocus();
    }

    const handleLoseFocus = () => {
        const domainListChanged = !arraysEqual(currentChosenDomain, chosenDomain);
        const eventNameListChanged = !arraysEqual(currentChosenEventName, chosenEventName);

        // Always refetch so the chosen date range is kept when the last filter is removed.
        if (domainListChanged || eventNameListChanged) {
            setLoadingActive(true);
            setUpdateLoadFlag(!updateLoadFlag);
        }
    };


    // Tabs
    const [tabsVisibility, setTabsVisibility] = useState({
        "errors": true,
        "overview": false,
    });

    const handleTabsVisibilityChange = (val) => {

        switch (val) {
            case 'errors':
                setTabsVisibility({ "errors": true, "overview": false });

                break;
            case 'overview':
                setTabsVisibility({ "errors": false, "overview": true });
                break;
            default:
        }

        setActiveTab(val);
    }

    const setDefaultDateRange = () => {
        setStartDate(dayjs().add(-7, 'd').set('hour', 0).set('minute', 0).set('second', 0));
        setEndDate(dayjs().set('hour', 23).set('minute', 59).set('second', 59));
    }



    const refreshFilters = () => {

        setfilterButtons(true);
        setChosenDomain([]);
        setChosenEventName([]);
        setErrorsList(initialErrorsList);
        setDefaultDateRange();
        setSnapshotsList(initialSnapshotsList);
        setDifferenceOneDayOrOneHour(null);
        setCurrentChosenDomain([]);
        setCurrentChosenEventName([]);
    }

    const renderIssueContent = (row) => {
        const category = getErrorCategory(row);

        if (category === ERROR_CATEGORY.COOKIE) {
            const params = getNormalizedParameters(row);
            if (params.length === 0) return <></>;
            return (
                <div key="cookie_issue" className="error-details-desc">
                    <h6><strong>Issue</strong></h6>
                    {params.map((param, idx) => (
                        <React.Fragment key={`cookie-param-${idx}`}>
                            {param?.description && (
                                <div className="param-error">
                                    <strong>Error:</strong> {param.description}
                                </div>
                            )}
                            {param?.parameter_name && (
                                <div className="parameters">
                                    <strong>Cookie:</strong> {param.parameter_name}
                                </div>
                            )}
                            <div>
                                <div className="expected-value">
                                    <p><strong>Expected:</strong> {formatCookieExpected(param?.expected_value)}</p>
                                </div>
                                <div className="actual-value">
                                    <p><strong>Actual:</strong> {formatCookieActual(param?.actual_value)}</p>
                                </div>
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            );
        }

        if (category === ERROR_CATEGORY.FLOW_MONITOR) {
            const params = getNormalizedParameters(row);
            return (
                <div key="flow_monitor_issue" className="error-details-desc">
                    <h6><strong>Issue</strong></h6>
                    {params.length > 0 ? params.map((param, idx) => {
                        const expectedValue = param?.expected_value;
                        const actualValue = param?.actual_value;
                        const expectedDisplay = expectedValue == null || expectedValue === '' ? '-' : String(expectedValue);
                        const actualDisplay = actualValue == null || actualValue === '' ? '-' : String(actualValue);
                        return (
                            <React.Fragment key={`network-param-${idx}`}>
                                {param?.description && (
                                    <div className="param-error">
                                        <strong>Detail:</strong> {param.description}
                                    </div>
                                )}
                                {param?.parameter_name && (
                                    <div className="parameters">
                                        <strong>Checked field:</strong> {param.parameter_name}
                                    </div>
                                )}
                                <div>
                                    <div className="expected-value">
                                        <p><strong>Expected:</strong> {expectedDisplay}</p>
                                    </div>
                                    <div className="actual-value">
                                        <p><strong>Actual:</strong> {actualDisplay}</p>
                                    </div>
                                </div>
                            </React.Fragment>
                        );
                    }) : (
                        <div className="param-error">
                            <strong>Detail:</strong> {row?.error_description || '-'}
                        </div>
                    )}
                </div>
            );
        }

        const templateString = JSON.stringify(JSON.parse(row?.template || "{}"), null, 4);

        switch (row?.error_type) {
            case "missing_object":
                return (
                    <div key="missing_object" className="error-details-desc">
                        <strong>Example of the expected object</strong>
                        <div className="dl-template-grn">
                            <pre>{templateString}</pre>
                        </div>
                    </div>
                );
    
            case "incorrect_parameters":
                // Check if parameters array exists, use first item in the array
                const param = row.parameters && row.parameters.length > 0 ? row.parameters[0] : {};
                
                const description = param.description || row.description;
                const error = param.error || row.error;
                const parameterName = param.parameter_name || row.parameter_name || "";
                const expectedType = param.expected_type || row.expected_type;
                const expectedValue = param.expected_value || row.expected_value;
                const actualType = param.actual_type || row.actual_type;
                const actualValue = param.actual_value || row.actual_value;
    
                const incorrectTypeErrDesc = (
                    <>
                        <div className="expected-type">
                            <p><strong>Expected type:</strong> {expectedType}</p>
                        </div>
                        <div className="actual-type">
                            <p><strong>Found type:</strong> {actualType}</p>
                        </div>
                        <div className="actual-value">
                            <p><strong>Found value:</strong> {actualValue}</p>
                        </div>
                    </>
                );
    
                const incorrectValueErrDesc = (
                    <>
                        <div className="expected-value">
                            <p><strong>Expected value:</strong> {expectedValue}</p>
                        </div>
                        <div className="actual-value">
                            <p><strong>Found Value:</strong> {actualValue}</p>
                        </div>
                    </>
                );
    
                let errorContent = null;
    
                switch (error) {
                    case "incorrect_type":
                        errorContent = incorrectTypeErrDesc;
                        break;
                    case "incorrect_value":
                        errorContent = incorrectValueErrDesc;
                        break;
                    default:
                }
    
                return (
                    <div key="incorrect_parameters" className="error-details-desc">
                        <h6><strong>Issue</strong></h6>
                        <div className="param-error">
                            <strong>Error:</strong> {description}
                        </div>
                        {parameterName && (
                            <div className="parameters">
                                <strong>Parameter:</strong> {parameterName}
                            </div>
                        )}
                        {errorContent && (
                            <div>
                                {errorContent}
                            </div>
                        )}
                    </div>
                );
    
            default:
                return (<></>);
        }
    }

    const dataLayerContentPreview = (datalayerString) => {
        let formattedString = "";

        switch (datalayerString) {
            case "":
            case "{}":
            case "[]":
            case "N/A":
                formattedString = "The object is missing";
                break;
            default:
                formattedString = JSON.stringify(JSON.parse(datalayerString), null, 2);
        }

        return formattedString;
    }

    const isEmptyJSONLiteral = (raw, ...empties) => {
        const trimmed = String(raw || '').trim();
        return !trimmed || trimmed === 'N/A' || empties.includes(trimmed);
    };

    const prettyPrintActualObject = (raw, emptyMessage, emptyLiterals) => {
        if (isEmptyJSONLiteral(raw, ...emptyLiterals)) return emptyMessage;
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) ? parsed.length === 0 : parsed && Object.keys(parsed).length === 0) {
                return emptyMessage;
            }
            return JSON.stringify(parsed, null, 2);
        } catch {
            return String(raw);
        }
    };

    const getSnapshotPanelTitle = (category) => {
        switch (category) {
            case ERROR_CATEGORY.COOKIE: return 'Cookies on the page';
            case ERROR_CATEGORY.FLOW_MONITOR: return 'Captured network activity';
            default: return 'Snapshot of your Datalayer';
        }
    };

    const getTypeOfErrorText = (row, category) => {
        switch (category) {
            case ERROR_CATEGORY.COOKIE: {
                const params = getNormalizedParameters(row);
                const anyMissing = params.some((p) => p?.error === 'missing_cookie');
                return anyMissing ? 'Cookie not found' : 'Cookie value mismatch';
            }
            case ERROR_CATEGORY.FLOW_MONITOR:
                return FLOW_MONITOR_TYPE_LABEL[row?.error_type] || row?.error_description || '-';
            default:
                return row?.error_description || '-';
        }
    };

    const getErrorMessageText = (row, category) => {
        const params = getNormalizedParameters(row);
        const firstParamDescription = params[0]?.description;
        switch (category) {
            case ERROR_CATEGORY.COOKIE:
                return firstParamDescription || row?.description || row?.error_description || '-';
            case ERROR_CATEGORY.FLOW_MONITOR:
                return firstParamDescription || row?.error_description || row?.description || '-';
            default:
                return removeRedundantErrorMessage(row?.description || row?.error_description || '-');
        }
    };


    const handleEventNameField = (e) => setEventNameFilter(e.target.value);

    const getErrorRowKey = (row, index = 0) => {
        if (row?.__rowKey) return row.__rowKey;
        return `error-${index}-${row?.event_name || ''}-${row?.timestamp || ''}-${row?.url || ''}`;
    };

    const getSnapshotRowKey = (row, index = 0) => {
        if (row?.__rowKey) return row.__rowKey;
        return `snapshot-${index}-${row?.page_type || ''}-${row?.url || ''}`;
    };

    const visibleErrorsList = useMemo(() => {
        // Admins see step-diagnostic rows unless they hide them via the toggle.
        // Non-admins never see them.
        if (adminUser && showStepDiagnostics) return errorsList;
        return errorsList.filter((row) => !isStepFailure(row));
    }, [errorsList, adminUser, showStepDiagnostics]);

    const keyedErrorsList = useMemo(
        () => visibleErrorsList.map((row, index) => ({ ...row, __rowKey: getErrorRowKey(row, index) })),
        [visibleErrorsList],
    );

const keyedSnapshotsList = useMemo(
    () => snapshotsList.map((row, index) => ({
        ...row,
        timestamp: new Date(row.timestamp)
            .toLocaleString('sv-SE', { timeZone: 'Europe/Amsterdam' })
            .slice(0, 16), 
        __rowKey: getSnapshotRowKey(row, index)
    })),
    [snapshotsList],
);
    // Build the detail panel's display values once per selected error (not per render).
    const reviewBlob = useMemo(() => {
        // Full blob; de-duplicated rows are empty, so fall back to the hash map.
        const raw = errorToReview?.datalayer_string
            || datalayerBlobMapRef.current[errorToReview?.datalayer_hash]
            || '';
        const category = getErrorCategory(errorToReview);
        const isUnexpectedNetwork =
            category === ERROR_CATEGORY.FLOW_MONITOR &&
            errorToReview?.error_type === 'unexpected_network_events';
        const parsed = safeParseJSON(raw); // for the network list + Copy/Download

        // Compute only the value the panel shows for this category.
        let textValue = null;
        let networkItems = null;
        if (isUnexpectedNetwork) {
            networkItems = Array.isArray(parsed) ? parsed : [];
        } else if (raw.length > MAX_DATALAYER_DISPLAY_CHARS) {
            // Big blob: slice raw, skip the parse+stringify that caused the freeze.
            textValue = truncateForDisplay(raw);
        } else if (category === ERROR_CATEGORY.COOKIE) {
            textValue = prettyPrintActualObject(raw, 'No cookies were set on the page', ['{}']);
        } else if (category === ERROR_CATEGORY.FLOW_MONITOR) {
            textValue = prettyPrintActualObject(raw, 'No network requests were captured', ['{}', '[]']);
        } else {
            textValue = dataLayerContentPreview(raw);
        }

        // Safety cap for the small / pretty-printed branches too.
        if (textValue !== null) textValue = truncateForDisplay(textValue);

        return { raw, parsed, category, isUnexpectedNetwork, textValue, networkItems };
    }, [errorToReview]);

    // Snapshot-review textarea value, stringified once per selection and capped.
    const snapshotText = useMemo(
        () => truncateForDisplay(JSON.stringify(snapshotToReview?.event || {}, null, 4)),
        [snapshotToReview],
    ); 
    const handleAreaUpdate = (row, type) => {
        if (type === 'snapshot') {
            const rowKey = row?.__rowKey || getSnapshotRowKey(row);
            if (snapshotToReviewKey === rowKey) setSnapshotExpanded(!snapshotExpanded);
            else setSnapshotExpanded(true);
            setSnapshotToReview(row);
            setSnapshotToReviewKey(rowKey);
        } else if (type === 'error') {
            const rowKey = row?.__rowKey || getErrorRowKey(row);
            if (errorToReviewKey === rowKey) {
                setErrorExpanded(!errorExpanded);
            } else {
                setErrorExpanded(true);
            }
            setErrorToReview(row);
            setErrorToReviewKey(rowKey);
            const issueContentData = renderIssueContent(row);
            setIssueContent(issueContentData);
        }
    };

    const CaretComponent = ({ row, isExpanded, type }) => {
        const handleClick = (event) => {
            event.stopPropagation();
            handleAreaUpdate(row, type);
        };

        return (
            <div onClick={handleClick} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, margin: 0 }}>
                {isExpanded ? <CaretUpFill size={14} /> : <CaretDownFill size={14} />}
            </div>
        );
    };

    function extractHostname(url) {
        const regex = /^(?:https?:\/\/)?(?:[^@\/\/]+@)?(?:www\.)?([^:\/\/]+)/;
        const match = url?.match(regex);
        return match ? match[1] : null;
    }
    function extractDomainAndPath(rawUrl, fallbackHostname = '') {
        const fallbackPath = '';
        const raw = String(rawUrl || '').trim();

        if (!raw) {
            return {
                domain: String(fallbackHostname || '').trim(),
                path: fallbackPath,
                url: '',
            };
        }

        const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

        try {
            const parsed = new URL(candidate);
            return {
                domain: parsed.hostname || String(fallbackHostname || '').trim(),
                path: `${parsed.pathname || '/'}${parsed.search || ''}`,
                url: raw,
            };
        } catch {
            return {
                domain: extractHostname(raw) || String(fallbackHostname || '').trim(),
                path: fallbackPath,
                url: raw,
            };
        }
    }

    const oneLineEllipsis = {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
        width: '100%',
    };

    const removeRedundantErrorMessage = (text) => {
        const redundantMessage = ' This element may have been renamed, removed, or changed on your website.';
        return String(text || '').endsWith(redundantMessage)
            ? text.slice(0, -redundantMessage.length).trim()
            : text;
    };

    const getErrorPreview = (text, extendPreview = false) => {
        const normalizedText = String(text || '-').replace(/\s+/g, ' ').trim();
        if (normalizedText === '-') return normalizedText;

        const stepMatch = normalizedText.match(/^(Step\s+\d+\s+\([^)]*\)\s+failed)/i);
        if (stepMatch?.[1]) {
            if (extendPreview) {
                const extendedLength = Math.min(normalizedText.length, stepMatch[1].length + 70);
                const extendedText = normalizedText.slice(0, extendedLength).trimEnd();
                return normalizedText.length > extendedLength
                    ? `${extendedText}...`
                    : extendedText;
            }

            return normalizedText.length > stepMatch[1].length
                ? `${stepMatch[1]}...`
                : stepMatch[1];
        }

        const fallbackLength = 60;
        return normalizedText.length > fallbackLength
            ? `${normalizedText.slice(0, fallbackLength).trimEnd()}...`
            : normalizedText;
    };

    const errorsListColumns = [
        {
            title: 'Hostname',
            dataIndex: 'url',
            key: 'hostname',
            width: isLargeViewport ? '16%' : '14%',
            ellipsis: true,
            render: (_, row) => {
                const hostname = extractHostname(row?.url) || '-';
                return <div style={oneLineEllipsis} title={hostname}>{hostname}</div>;
            },
            sorter: (a, b) => (extractHostname(a?.url) || '').localeCompare(extractHostname(b?.url) || ''),
        },
        {
            title: 'Event name',
            dataIndex: 'event_name',
            key: 'event_name',
            width: isLargeViewport ? '16%' : '14%',
            ellipsis: true,
            render: (text) => <div style={oneLineEllipsis} title={text || '-'}>{text || '-'}</div>,
            sorter: (a, b) => (a?.event_name || '').localeCompare(b?.event_name || ''),
        },
        {
            title: 'Error',
            dataIndex: 'description',
            key: 'description',
            width: isLargeViewport ? '34%' : '24%',
            ellipsis: true,
            render: (_, row) => {
                const errorText = row?.description || row?.error_description || '-';
                const errorPreview = getErrorPreview(errorText, isLargeViewport);
                return <div style={oneLineEllipsis} title={errorText}>{errorPreview}</div>;
            },
            sorter: (a, b) => (a?.description || a?.error_description || '').localeCompare(b?.description || b?.error_description || ''),
        },
        {
            title: 'Parameter',
            dataIndex: 'parameter_name',
            key: 'parameter_name',
            width: isLargeViewport ? '15%' : '20%',
            ellipsis: true,
            render: (_, row) => {
                const parameterText = row?.parameters?.[0]?.parameter_name || row?.parameter_name || '-';
                return (
                    <div style={oneLineEllipsis} title={parameterText}>
                        {parameterText}
                    </div>
                );
            },
            sorter: (a, b) => {
                const aVal = a?.parameters?.[0]?.parameter_name || a?.parameter_name || '';
                const bVal = b?.parameters?.[0]?.parameter_name || b?.parameter_name || '';
                return aVal.localeCompare(bVal);
            },
        },
        {
            title: 'Last time occurred',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: isLargeViewport ? '16%' : '18%',
            ellipsis: true,
            render: (text) => <div style={oneLineEllipsis} title={text || '-'}>{text || '-'}</div>,
            sorter: (a, b) => (a?.timestamp || '').localeCompare(b?.timestamp || ''),
        },
        {
            title: '',
            dataIndex: '__rowKey',
            key: 'expand',
            width: 56,
            render: (_, row) => (
                <CaretComponent
                    row={row}
                    isExpanded={errorToReviewKey === row?.__rowKey && errorExpanded}
                    type="error"
                />
            ),
        },
    ];

    const snapshotsListColumns = [
        {
            title: 'Hostname',
            dataIndex: 'url',
            key: 'url',
            width: '30%',
            ellipsis: true,
            render: (text) => <div style={oneLineEllipsis} title={text || '-'}>{text || '-'}</div>,
            sorter: (a, b) => (a?.url || '').localeCompare(b?.url || ''),
        },
        {
            title: 'Event name',
            dataIndex: 'page_type',
            key: 'page_type',
            width: '30%',
            ellipsis: true,
            render: (text) => <div style={oneLineEllipsis} title={text || '-'}>{text || '-'}</div>,
            sorter: (a, b) => (a?.page_type || '').localeCompare(b?.page_type || ''),
        },
         {
            title: 'Scenario',
            dataIndex: 'scenario_name',
            key: 'scenario_name',
            width: '30%',
            ellipsis: true,
            render: (text) => <div style={oneLineEllipsis} title={text || '-'}>{text || '-'}</div>,
            sorter: (a, b) => (a?.scenario_name || '').localeCompare(b?.scenario_name || ''),
        },
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: '30%',
            ellipsis: true,
            render: (text) => <div style={oneLineEllipsis} title={text || '-'}>{text || '-'}</div>,
            sorter: (a, b) => (a?.timestamp || '').localeCompare(b?.timestamp || ''),
        },
        {
            title: '',
            dataIndex: '__rowKey',
            key: 'expand',
            width: 56,
            render: (_, row) => (
                <CaretComponent
                    row={row}
                    isExpanded={snapshotToReviewKey === row?.__rowKey && snapshotExpanded}
                    type="snapshot"
                />
            ),
        },
    ];

    const lastHourTimestamp = () => {
        const currentDate = new Date();
        currentDate.setHours(currentDate.getHours() - 1);
        return currentDate.getTime();
    }


    useEffect(() => {
        // toggle the apply button based on the selected filters
        // If any filter is added enable the buttons 

        const filterButtonsDisabled = !(chosenDomain || chosenEventName);
        setfilterButtons(filterButtonsDisabled);

    }, [chosenDomain, chosenEventName]);


    const getFilterObject = () => {
        let setDifferenceOneDayOrOneHourParam,
            chosenEventNameParam,
            chosenDomainParam,
            requestStartDate,
            requestEndDate;
    
        // If the company is changed reset all the filters
        if (companyID !== companyId) {
            setDifferenceOneDayOrOneHour(null);
            setChosenEventName([]);
            setChosenDomain([]);
    
            setCurrentChosenDomain([]);
            setCurrentChosenEventName([]);
    
            setClientTimeStamp("Europe/Amsterdam")
            
            // Create the reset date values locally
            const resetStartDate = dayjs().add(-7, 'd');
            const resetEndDate = dayjs();
            
            // Set the state with the reset values
            setStartDate(resetStartDate);
            setEndDate(resetEndDate);
            
            // Use the local variables for the request
            requestStartDate = resetStartDate;
            requestEndDate = resetEndDate;
            setDifferenceOneDayOrOneHourParam = null;
            chosenEventNameParam = "";
            chosenDomainParam = "";
        } else {
            requestStartDate = startDate;
            requestEndDate = endDate;
            setDifferenceOneDayOrOneHourParam = differenceOneDayOrOneHour;
            chosenEventNameParam = chosenEventName;
            chosenDomainParam = chosenDomain;
            setCurrentChosenDomain(chosenDomain);
            setCurrentChosenEventName(chosenEventName);
        }

        let formattedStartDate = requestStartDate.format(timestampFormat);
        let formattedEndDate = requestEndDate.format(timestampFormat);
        
        // Check if dates are the same (ignoring time component)
        if (requestStartDate.format('YYYY-MM-DD') === requestEndDate.format('YYYY-MM-DD')) {
            formattedStartDate = requestStartDate.startOf('day').format(timestampFormat);
            formattedEndDate = requestEndDate.endOf('day').format(timestampFormat);
        }
    
        const reqObj = {
            companyID: companyId,
            startDate: requestStartDate.format(timestampFormat),
            endDate: requestEndDate.format(timestampFormat),
            chosenEventName: chosenEventNameParam,
            chosenDomain: chosenDomainParam,
            differenceOneDayOrOneHour: setDifferenceOneDayOrOneHourParam,
        };
    
        return reqObj;
    };
    

    // Then modify the useEffect that processes API data:
useEffect(() => {
        (async () => {
            setLoadingActive(true); // Start the loading spinner when the request starts
            setErrorExpanded(false);
            setSnapshotExpanded(false);
    
            const session = await DashboardService.checkSession();
            const sessionData = await session?.json();
            const sessionExists = sessionData?.session_exists;
    
            if (sessionExists) {
                let requestData;
                if (startDate && endDate) {
                    // If the profile has changed, reset all filters
                    if (companyID !== companyId) {
                        setInititalLoad(true);
                        setChosenDomain([]);
                        setChosenEventName([]);
                        setErrorsList([]);
                        setDomainsList([]);
                        setEventNameList([]);
                        setSnapshotsList([]);
                        setEventNameFilter("");
                        
                        // Use getFilterObject which correctly handles date reset
                        const filterObject = getFilterObject();
                        requestData = {
                            domain: "",
                            event_name: "",
                            differenceOneDayOrOneHour: null,
                            start_date: filterObject.startDate,
                            end_date: filterObject.endDate,
                        };
                    } else {
                        // If the profile has not changed, prepare the request using the current filters
                        const filterObject = getFilterObject();
                        requestData = {
                            domain: filterObject.chosenDomain,
                            event_name: filterObject.chosenEventName,
                            differenceOneDayOrOneHour: filterObject.differenceOneDayOrOneHour,
                            start_date: filterObject.startDate,
                            end_date: filterObject.endDate,
                        };
                    }
    
                    const response = await DashboardService.getDLGDashboardData(requestData);
                    const respData = await response.json();
                    const datasetExists = respData?.dataset_exists;
    
                    if (datasetExists) {
                        // If the dataset exists, update the data
                        setDatasetFound(true);
                        setDomainsList((respData?.context?.company_domains ?? []).map(elem => ({ domain: elem?.domain })));
                        setEventNameList((respData?.context?.event_names ?? []).map(elem => ({ event_name: elem?.event_name })));
                        const fetchedErrors = respData?.context?.errors ?? [];
                        // Rebuild the hash -> blob lookup from the de-duplicated rows.
                        for (const e of fetchedErrors) {
                            if (e?.datalayer_string && e?.datalayer_hash) {
                                datalayerBlobMapRef.current[e.datalayer_hash] = e.datalayer_string;
                            }
                        }
                        setErrorsList(() => fetchedErrors);
                        setSnapshotsList(() => respData?.context?.templates ?? []);

                        // Set the BQ_client_timestamp
                        const BQClientTimestamp = respData?.context?.BQ_client_timestamp;
                        setClientTimeStamp(BQClientTimestamp);

                        if (inititalLoad || companyID !== companyId) {
                            setInitialErrorsList(() => fetchedErrors);
                            setInitialSnapshotsList(() => respData?.context?.templates ?? []);
                        }
    
                        setInititalLoad(false);
                        setFiltersDisabled(false); // Enable filters when data is received
                    } else {
                        setDatasetFound(false);
                    }
    
                    setCompanyID(companyId); // Update the current company ID
                } else {
                    alert("Please select a valid start and end date!");
                }
    
                setLoadingActive(false); // Stop the loading spinner when the request is complete
            } else {
                localStorageService.clearLocalStorage();
                navigate('/login');
            }
        })();
    }, [companyId, updateLoadFlag]);

    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            const MAX_WIDTH = 440;
            const scale = Math.min(1, MAX_WIDTH / img.width);
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            setLogoBase64(canvas.toDataURL('image/png'));
            setLogoAspectRatio(img.width / img.height);
        };
        img.src = `${process.env.PUBLIC_URL}/code-cube.io-marketing-observability-white-blue.png`;
    }, []);

    const arraysEqual = (arr1 = [], arr2 = []) => {
        if (arr1.length !== arr2.length) return false;

        for (let i = 0; i < arr1.length; i++) { 
            if (arr1[i] !== arr2[i]) return false;
        }

        return true;
    };

    const sortOptionsWithSelectedOnTop = (options = [], selectedValues = []) => {
        return [...options].sort((a, b) => {
            const isASelected = selectedValues.includes(a.value);
            const isBSelected = selectedValues.includes(b.value);

            if (isASelected === isBSelected) {
                if (isASelected) {
                    return selectedValues.indexOf(a.value) - selectedValues.indexOf(b.value);
                }

                const labelA = String(a?.label || '');
                const labelB = String(b?.label || '');
                return labelA.localeCompare(labelB);
            }

            return isASelected ? -1 : 1;
        });
    };

    const domainOptions = useMemo(
        () => domainsList
            .map((elem) => ({
                label: elem?.domain,
                value: elem?.domain,
            }))
            .filter((option) => Boolean(option.value)),
        [domainsList],
    );

    const eventsOptions = useMemo(
        () => eventNameList
            .map((elem) => ({
                label: elem?.event_name,
                value: elem?.event_name,
            }))
            .filter((option) => Boolean(option.value)),
        [eventNameList],
    );



    const handleUpdateClick = () => setUpdateLoadFlag(!updateLoadFlag);

    const handleCopyClick = (value) => {
        const textarea = document.createElement('textarea');
        textarea.value = JSON.stringify(value || {}, null, 2);
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    };
    const DISCLAIMER = 'This document contains a snapshot of your dataLayer as generated at the time of download. For the most up-to-date information, please log in to your account at:';
    const DISCLAIMER_URL = 'https://portal.code-cube.io';
    const DISCLAIMER_LINK_TEXT = 'portal.code-cube.io';

    // Renders the disclaimer text + a clickable URL line in the PDF.
    // Returns the total Y height consumed so the caller can position the separator.
    const addPDFDisclaimer = (doc, x, startY, maxWidth) => {
        doc.setFontSize(7);
        doc.setTextColor(149, 165, 166);
        const textLines = doc.splitTextToSize(DISCLAIMER, maxWidth);
        doc.text(textLines, x, startY);
        const urlY = startY + textLines.length * 3.5;
        doc.setTextColor(149, 165, 166);
        doc.text(DISCLAIMER_LINK_TEXT, x, urlY);
        const urlWidth = doc.getTextWidth(DISCLAIMER_LINK_TEXT);
        doc.link(x, urlY - 2.5, urlWidth, 3.5, { url: DISCLAIMER_URL });
        return textLines.length + 1; // number of lines used (text lines + URL line)
    };

    const addPDFLogoHeader = (doc, title) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const LOGO_HEIGHT = 12;
        const logoWidth = logoBase64 ? Math.min(LOGO_HEIGHT * logoAspectRatio, 55) : 0;

        // Code-Cube branded dark navy header bar
        doc.setFillColor(9, 0, 61);
        doc.rect(0, 0, pageWidth, 22, 'F');

        // Logo (alias ensures image data is embedded only once across all pages)
        if (logoBase64) {
            doc.addImage(logoBase64, 'PNG', 6, 5, logoWidth, LOGO_HEIGHT, 'cc-logo');
        }

        // Report title (white text, right-aligned, vertically centered)
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text(title, pageWidth - 6, 12, { align: 'right' });
    };

    const downloadAsPDF = (data, filename) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const HEADER_HEIGHT = 22;
        const CONTENT_START = HEADER_HEIGHT + 8;
        const FOOTER_HEIGHT = 20;

        const addNewPage = (fontSize, r, g, b) => {
            doc.addPage();
            addPDFLogoHeader(doc, 'DataLayer Event Details');
            doc.setFontSize(fontSize);
            doc.setTextColor(r, g, b);
            return CONTENT_START;
        };

        // Code-Cube branded header
        addPDFLogoHeader(doc, 'DataLayer Event Details');

        // Metadata
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`, 10, CONTENT_START);
        doc.text(`Company: ${companyInfo?.name || 'N/A'}`, 10, CONTENT_START + 7);

        const separatorY = CONTENT_START + 14;

        // Blue accent separator
        doc.setDrawColor(9, 0, 61);
        doc.setLineWidth(0.3);
        doc.line(10, separatorY, pageWidth - 10, separatorY);

        // JSON content
        doc.setFontSize(8);
        doc.setTextColor(44, 62, 80);
        const jsonString = JSON.stringify(data, null, 2);
        const lines = doc.splitTextToSize(jsonString, pageWidth - 20);

        let yPosition = separatorY + 6;
        for (let i = 0; i < lines.length; i++) {
            if (yPosition > pageHeight - FOOTER_HEIGHT) {
                yPosition = addNewPage(8, 44, 62, 80);
            }
            doc.text(lines[i], 10, yPosition);
            yPosition += 4;
        }

        // Footer on all pages
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            addPDFDisclaimer(doc, 10, pageHeight - 15, pageWidth - 20);
            doc.setFontSize(7);
            doc.setTextColor(149, 165, 166);
            doc.text(
                `Page ${i} of ${totalPages}  |  code-cube.io`,
                pageWidth / 2,
                pageHeight - 3,
                { align: 'center' }
            );
        }

        doc.save(`${filename}.pdf`);
    };

    const flattenObject = (obj, prefix = '') => {
        return Object.keys(obj).reduce((acc, key) => {
            const newKey = prefix ? `${prefix}.${key}` : key;
            if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                Object.assign(acc, flattenObject(obj[key], newKey));
            } else if (Array.isArray(obj[key])) {
                acc[newKey] = JSON.stringify(obj[key], null, 2);
            } else {
                acc[newKey] = obj[key];
            }
            return acc;
        }, {});
    };

    const normalizeExportValue = (value) => {
        if (value == null) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };

    const colIndexToLetter = (index) => {
        let n = index;
        let result = '';
        while (n > 0) {
            const rem = (n - 1) % 26;
            result = String.fromCharCode(65 + rem) + result;
            n = Math.floor((n - 1) / 26);
        }
        return result;
    };

    const writeXLSXWorksheet = (worksheet, title, metadataLines, columns, rows, options = {}) => {
        const wideColumns = options.wideColumns || [];
        const columnWidths = options.columnWidths || [];
        const topSectionColCount = Math.max(columns.length, 1);
        const lastTopSectionCol = colIndexToLetter(topSectionColCount);

        const writeDisclaimerRow = (rowIdx) => {
            const disclaimerCell = worksheet.getCell(`A${rowIdx}`);
            disclaimerCell.value = {
                richText: [
                    { text: `Disclaimer: ${DISCLAIMER} `, font: { italic: true, size: 8, color: { argb: 'FF999999' }, name: 'Calibri' } },
                    { text: DISCLAIMER_LINK_TEXT, hyperlink: DISCLAIMER_URL, font: { italic: true, size: 8, color: { argb: 'FF999999' }, underline: true, name: 'Calibri' } },
                ],
            };
            disclaimerCell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left', indent: 4 };
            worksheet.getRow(rowIdx).height = 45;
            worksheet.mergeCells(`A${rowIdx}:${lastTopSectionCol}${rowIdx}`);
        };

        const logoRow = worksheet.getRow(1);
        logoRow.height = 45;
        logoRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' }, name: 'Calibri' };
        logoRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

        for (let col = 1; col <= topSectionColCount; col++) {
            const cell = worksheet.getCell(1, col);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF09003D' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        }

        if (logoBase64) {
            const base64Data = logoBase64.includes(',') ? logoBase64.split(',')[1] : logoBase64;
            const logoImgId = worksheet.workbook.addImage({ base64: base64Data, extension: 'png' });
            const logoHeight = 40;
            const logoWidth = Math.round(logoHeight * logoAspectRatio);
            const columnAWidthPx = 360;
            const logoOffsetPx = 0;
            const colFraction = Math.max(0, logoOffsetPx / columnAWidthPx);
            const rowFraction = 0;

            worksheet.addImage(logoImgId, {
                tl: { col: colFraction, row: rowFraction },
                ext: { width: logoWidth, height: logoHeight },
                editAs: 'oneCell',
            });
        }

        const titleCell = worksheet.getCell('A2');
        titleCell.value = title;
        titleCell.font = { bold: true, size: 13, color: { argb: 'FF09003D' }, name: 'Calibri' };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        worksheet.getRow(2).height = 45;

        const topMetadataLines = metadataLines.slice(0, 7);
        let currentRow = 3;
        topMetadataLines.forEach((line) => {
            const metaCell = worksheet.getCell(`A${currentRow}`);
            metaCell.value = line;
            metaCell.font = { size: 9, color: { argb: 'FF777777' }, name: 'Calibri' };
            metaCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            worksheet.getRow(currentRow).height = 45;
            currentRow++;
        });

        const disclaimerRowIdx = 10;
        writeDisclaimerRow(disclaimerRowIdx);

        const spacingRow = worksheet.getRow(11);
        spacingRow.values = [];
        spacingRow.height = 45;

        const headerRowIdx = 12;
        const headerRow = worksheet.getRow(headerRowIdx);
        headerRow.height = 45;

        for (let col = 1; col <= topSectionColCount; col++) {
            const cell = headerRow.getCell(col);
            if (col <= columns.length) {
                cell.value = columns[col - 1];
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, name: 'Calibri' };
            }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF09003D' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        }

        columns.forEach((column, index) => {
            if (Number.isFinite(columnWidths[index])) {
                worksheet.getColumn(index + 1).width = columnWidths[index];
            } else if (wideColumns.includes(index)) {
                worksheet.getColumn(index + 1).width = 51;
            } else {
                worksheet.getColumn(index + 1).width = Math.max(column.length + 4, 16);
            }
        });

        let dataRowIdx = headerRowIdx + 1;
        rows.forEach((row, rowIndex) => {
            const dataRow = worksheet.getRow(dataRowIdx);
            dataRow.font = { name: 'Calibri', size: 10 };
            dataRow.height = 45;

            for (let col = 1; col <= topSectionColCount; col++) {
                const cell = dataRow.getCell(col);
                if (col <= columns.length) {
                    cell.value = normalizeExportValue(row[columns[col - 1]]);
                } else {
                    cell.value = null;
                }

                if (rowIndex % 2 === 1) {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F7' } };
                }

                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            }

            dataRowIdx++;
        });

        const bottomSpacingRow = worksheet.getRow(dataRowIdx);
        bottomSpacingRow.values = [];
        bottomSpacingRow.height = 45;
        writeDisclaimerRow(dataRowIdx + 1);
    };

    const downloadAsXLSX = async (data, filename) => {
        const wb = new ExcelJS.Workbook();
        wb.creator = 'Code-Cube.io';
        const ws = wb.addWorksheet('Event Details');

        const metaLines = [
            `Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`,
            `Company: ${companyInfo?.name || 'N/A'}`,
        ];
        const detailData = Array.isArray(data) ? (data[0] ?? {}) : data;
        const domainPath = extractDomainAndPath(detailData?.url, detailData?.hostname);
        const flatData = {
            domain: domainPath.domain,
            path: domainPath.path,
            ...flattenObject(detailData),
        };
        const rows = Object.entries(flatData).map(([key, value]) => ({
            Key: key,
            Value: value,
        }));

        writeXLSXWorksheet(
            ws,
            'DataLayer Event Details',
            metaLines,
            ['Key', 'Value'],
            rows,
            { columnWidths: [51, 51] },
        );

        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.xlsx`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const downloadAllAsPDFEveryPage = () => {
        const doc = new jsPDF();
        const dataList = activeTab === 'errors' ? visibleErrorsList : snapshotsList;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const reportTitle = `DataLayer Guard - ${activeTab === 'errors' ? 'Errors' : 'Snapshots'} Report`;
        const HEADER_HEIGHT = 22;
        const CONTENT_START = HEADER_HEIGHT + 8;
        const FOOTER_HEIGHT = 20;

        // addNewPage resets font/color after header (header ends with white text color)
        const addNewPage = (fontSize, r, g, b) => {
            doc.addPage();
            addPDFLogoHeader(doc, reportTitle);
            doc.setFontSize(fontSize);
            doc.setTextColor(r, g, b);
            return CONTENT_START;
        };

        // Cover page: header + metadata
        addPDFLogoHeader(doc, reportTitle);
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`, 10, CONTENT_START);
        doc.text(`Company: ${companyInfo?.name || 'N/A'}`, 10, CONTENT_START + 6);
        doc.text(`Date Range: ${startDate.format('YYYY-MM-DD')} to ${endDate.format('YYYY-MM-DD')}`, 10, CONTENT_START + 12);
        doc.text(`Total Events: ${dataList.length}`, 10, CONTENT_START + 18);

        const separatorY = CONTENT_START + 25;

        doc.setDrawColor(9, 0, 61);
        doc.setLineWidth(0.3);
        doc.line(10, separatorY, pageWidth - 10, separatorY);

        const firstEventStartY = separatorY + 6;

        dataList.forEach((item, index) => {
            // First event continues on the cover page; subsequent events get a new page with the logo header
            let yPosition;
            if (index === 0) {
                yPosition = firstEventStartY;
                doc.setFontSize(11);
                doc.setTextColor(9, 0, 61);
            } else {
                yPosition = addNewPage(11, 9, 0, 61);
            }

            const itemData = activeTab === 'errors'
                ? JSON.parse(item?.datalayer_string || '{}')
                : item?.event || {};

            // Event title
            doc.setFontSize(11);
            doc.setTextColor(9, 0, 61);
            doc.text(`Event ${index + 1}: ${item?.event_name || item?.page_type || 'Unknown'}`, 10, yPosition);
            yPosition += 6;

            // Event metadata
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            if (item?.url || item?.hostname) {
                const urlText = `Domain: ${item?.url || item?.hostname}`;
                const urlLines = doc.splitTextToSize(urlText, pageWidth - 20);
                for (let line of urlLines) {
                    if (yPosition > pageHeight - FOOTER_HEIGHT) {
                        yPosition = addNewPage(8, 100, 100, 100);
                    }
                    doc.text(line, 10, yPosition);
                    yPosition += 4;
                }
            }
            if (item?.timestamp) {
                if (yPosition > pageHeight - FOOTER_HEIGHT) {
                    yPosition = addNewPage(8, 100, 100, 100);
                }
                doc.text(`Timestamp: ${dayjs(item.timestamp).format('YYYY-MM-DD HH:mm:ss')}`, 10, yPosition);
                yPosition += 5;
            }

            // JSON content
            doc.setFontSize(8);
            doc.setTextColor(44, 62, 80);
            const jsonString = JSON.stringify(itemData, null, 2);
            const jsonLines = doc.splitTextToSize(jsonString, pageWidth - 20);

            for (let i = 0; i < jsonLines.length; i++) {
                if (yPosition > pageHeight - FOOTER_HEIGHT) {
                    yPosition = addNewPage(8, 44, 62, 80);
                }
                doc.text(jsonLines[i], 10, yPosition);
                yPosition += 4;
            }
        });

        // Footer on all pages
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            addPDFDisclaimer(doc, 10, pageHeight - 15, pageWidth - 20);
            doc.setFontSize(7);
            doc.setTextColor(149, 165, 166);
            doc.text(
                `Page ${i} of ${totalPages}  |  code-cube.io`,
                pageWidth / 2,
                pageHeight - 3,
                { align: 'center' }
            );
        }

        doc.save(`datalayer-guard-${activeTab}-${dayjs().format('YYYY-MM-DD')}.pdf`);
    };

    const downloadAllAsXLSX = async () => {
        const dataList = activeTab === 'errors' ? visibleErrorsList : snapshotsList;
        const wb = new ExcelJS.Workbook();
        wb.creator = 'Code-Cube.io';
        const ws = wb.addWorksheet(activeTab === 'errors' ? 'Errors' : 'Snapshots');

        const title = `DataLayer Guard - ${activeTab === 'errors' ? 'Errors' : 'Snapshots'} Report`;
        const metaLines = [
            `Generated: ${dayjs().format('YYYY-MM-DD HH:mm:ss')}`,
            `Company: ${companyInfo?.name || 'N/A'}`,
            `Date Range: ${startDate.format('YYYY-MM-DD')} to ${endDate.format('YYYY-MM-DD')}`,
            `Total Events: ${dataList.length}`,
        ];
        const rows = dataList.map((item) => {
            const itemData = activeTab === 'errors'
                ? JSON.parse(item?.datalayer_string || '{}')
                : item?.event || {};
            const domainPath = extractDomainAndPath(item?.url, item?.hostname);

            return {
                'Event Name': item?.event_name || item?.page_type || 'Unknown',
                Domain: domainPath.domain,
                Path: domainPath.path,
                URL: domainPath.url,
                Timestamp: item?.timestamp ? dayjs(item.timestamp).format('YYYY-MM-DD HH:mm:ss') : '',
                Data: JSON.stringify(itemData, null, 2),
            };
        });

        writeXLSXWorksheet(
            ws,
            title,
            metaLines,
            ['Event Name', 'Domain', 'Path', 'URL', 'Timestamp', 'Data'],
            rows,
            { columnWidths: [24, 22, 30, 40, 22, 51] },
        );

        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `datalayer-guard-${activeTab}-${dayjs().format('YYYY-MM-DD')}.xlsx`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const updateDateRange = (range) => {
        let newStartDate;
        let newEndDate;

        switch (range) {
            case 'last_hour':
                newStartDate = dayjs().subtract(1, 'hour');
                newEndDate = dayjs();
                break;
            case 'last_24_hours':
                newStartDate = dayjs().subtract(24, 'hour');
                newEndDate = dayjs();
                break;
            case 'last_7_days':
                newStartDate = dayjs().subtract(7, 'day').startOf('day');
                newEndDate = dayjs().set('hour', 23).set('minute', 59).set('second', 59);
                break;
            case 'last_14_days':
                newStartDate = dayjs().subtract(14, 'day').startOf('day');
                newEndDate = dayjs().set('hour', 23).set('minute', 59).set('second', 59);
                break;
            default:
                newStartDate = dayjs().subtract(7, 'day').startOf('day');
                newEndDate = dayjs().set('hour', 23).set('minute', 59).set('second', 59);
        }

        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setDifferenceOneDayOrOneHour(range === 'last_hour' ? 'last_hour' : null);
        setUpdateLoadFlag(!updateLoadFlag);
    };

    return (
        <Content>
            <LoadingScreen isActive={loadingActive} />
            {datasetFound ? (
                <Space direction="vertical" className="container-content" size="large">
                    <Flex justify="space-between" align="flex-start">
                        <PageHeader categoryName="dashboard" pageName="DataLayer Guard dashboard" />
                    </Flex>
                    <div>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}> 
                            <Row>
                                <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                                    <Tabs
                                        items={[
                                            {
                                                name: "errors",
                                                title: "encountered errors",
                                                onClickFunction: () => handleTabsVisibilityChange("errors"),
                                            },
                                            {
                                                name: "overview",
                                                title: "dataLayer overview",
                                                onClickFunction: () => handleTabsVisibilityChange("overview"),
                                            },
                                        ]}
                                        activeTab={activeTab}
                                    />
                                    <Dropdown
                                        menu={{
                                            items: [
                                                {
                                                    key: 'pdf',
                                                    label: 'Download PDF',
                                                    icon: <Download size={14} />,
                                                    onClick: downloadAllAsPDFEveryPage,
                                                },
                                                {
                                                    key: 'xls',
                                                    label: 'Download Excel',
                                                    icon: <Download size={14} />,
                                                    onClick: downloadAllAsXLSX,
                                                },
                                            ],
                                        }}
                                        placement="bottomRight"
                                    >
                                        <div
                                            className="text-area-copy-button"
                                            style={{
                                                position: 'relative',
                                                right: 'auto',
                                                top: 'auto',
                                                margin: 0,
                                                marginLeft: 'auto',
                                            }}
                                        >
                                            {/* <ThreeDots size={18} /> */}
                                            <Download size={18} />
                                        </div>
                                    </Dropdown>
                                </Flex>
                            </Row>

                            {/* filters */}

                            <Row className="filters-row">
                                <Flex style={{ width: '100%', flexWrap: 'wrap' }} gap="small" justify={'flex-start'} align={'center'}>
                                    <Space style={{ minWidth: '7rem' }} direction="vertical">
                                        <RangePicker
                                            presets={[
                                                ...rangePresets,
                                            ]}
                                            showTime={false}
                                            format={timestampFormatInput}
                                            onChange={onRangeChange}
                                            variant="filled"
                                            placeholder="time & date"
                                            defaultValue={[startDate, endDate]}
                                            value={[startDate, endDate]}
                                        />
                                    </Space>

                                    <Space style={{ flex: '1 1 8rem', minWidth: '8rem' }} direction="vertical">
                                        <Select
                                            popupMatchSelectWidth={false}
                                            mode="multiple"
                                            style={{ width: '100%' }}
                                            placeholder="domain"
                                            onChange={(e) => handleFilterChange(e, setChosenDomain)}
                                            options={sortOptionsWithSelectedOnTop(domainOptions, chosenDomain)}
                                            showSearch={true}
                                            maxTagCount={0}
                                            value={chosenDomain}
                                            disabled={filtersDisabled}
                                            onBlur={handleLoseFocus}
                                            onClear={() => handleClear(setChosenDomain)}
                                            variant="filled"
                                            suffixIcon={<ChevronDown />}
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            filterSort={(optionA, optionB) =>
                                                sortOptionsWithSelectedOnTop([optionA, optionB], chosenDomain)[0] === optionA
                                                    ? -1
                                                    : 1
                                            }
                                            autoClearSearchValue={false}
                                            searchValue={undefined}
                                            popupClassName="custom-dropdown"
                                        />
                                    </Space>

                                    <Space style={{ flex: '1 1 8rem', minWidth: '8rem' }} direction="vertical">
                                        <Select
                                            popupMatchSelectWidth={false}
                                            mode="multiple"
                                            style={{ width: '100%' }}
                                            placeholder="event name"
                                            onChange={(e) => handleFilterChange(e, setChosenEventName)}
                                            options={sortOptionsWithSelectedOnTop(eventsOptions, chosenEventName)}
                                            showSearch={true}
                                            maxTagCount={0}
                                            value={chosenEventName}
                                            disabled={filtersDisabled}
                                            onBlur={handleLoseFocus}
                                            onClear={() => handleClear(setChosenEventName)}
                                            variant="filled"
                                            suffixIcon={<ChevronDown />}
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            filterSort={(optionA, optionB) =>
                                                sortOptionsWithSelectedOnTop([optionA, optionB], chosenEventName)[0] === optionA
                                                    ? -1
                                                    : 1
                                            }
                                            autoClearSearchValue={false}
                                            searchValue={undefined}
                                            popupClassName="custom-dropdown"
                                        />
                                    </Space>

                                    <Col align="center" className="filters-refresh">
                                        <ArrowCounterclockwise
                                            size={22}
                                            onClick={() => refreshFilters()}
                                        />
                                    </Col>

                                    {adminUser && (
                                        <Col align="center" className="step-diagnostics-toggle" style={{ marginLeft: 'auto' }}>
                                            <Space size="small" align="center">
                                                <Switch
                                                    size="small"
                                                    checked={showStepDiagnostics}
                                                    onChange={handleToggleStepDiagnostics}
                                                />
                                                <span>Show step diagnostics</span>
                                            </Space>
                                        </Col>
                                    )}
                                </Flex>
                            </Row>
                        </Space>
                        <Space className={`charts-area ${tabsVisibility["errors"] ? "visible" : ""}`}>
                            <AntdTable
                                columns={errorsListColumns}
                                dataSource={keyedErrorsList}
                                rowKey="__rowKey"
                                pagination={{
                                    showSizeChanger: true,
                                    pageSizeOptions: ['10', '25', '50', '100'],
                                    defaultPageSize: 10,
                                }}
                                onRow={(record) => ({
                                    onClick: () => handleAreaUpdate(record, 'error'),
                                })}
                            />

                            {(() => {
                                const errorCategory = reviewBlob.category;
                                const panelTitle = getSnapshotPanelTitle(errorCategory);
                                const typeOfErrorText = getTypeOfErrorText(errorToReview, errorCategory);
                                const errorMessageText = getErrorMessageText(errorToReview, errorCategory);
                                const isUnexpectedNetwork = reviewBlob.isUnexpectedNetwork;
                                const parsedActualObject = reviewBlob.parsed;
                                const filenameCategoryPrefix =
                                    errorCategory === ERROR_CATEGORY.COOKIE ? 'cookie-check'
                                    : errorCategory === ERROR_CATEGORY.FLOW_MONITOR ? 'flow-monitor'
                                    : 'datalayer-error';
                                const downloadPayload = parsedActualObject ?? {};

                                let panelBody;
                                
                                if (isUnexpectedNetwork) {
                                    const items = reviewBlob.networkItems || [];
                                    panelBody = items.length === 0
                                        ? <div className="empty-actual-object">No network requests were captured</div>
                                        : <UnexpectedNetworkEventsList items={items} />;
                                } else {
                                    panelBody = (
                                        <textarea
                                            className="datalayer-string-snp"
                                            disabled={true}
                                            value={reviewBlob.textValue}
                                        />
                                    );
                                }                           
                                return (
                                    <div className={`mb-3 ${errorExpanded ? "slide-down" : "slide-up"}`}>
                                        <div className="row" style={{ display: "flex" }}>
                                            <div className="elems-list col-6 equal-height-div">
                                                <h5><strong>Errors in detail</strong></h5>
                                                <div className="error-details-desc">
                                                    <div><strong>Event:</strong> {errorToReview?.event_name}</div>
                                                    <div><strong>Scenario name:</strong> {errorToReview?.scenario_name}</div>
                                                    <div><strong>Type of Error:</strong> {typeOfErrorText}</div>
                                                    <div><strong>Error message:</strong> {errorMessageText}</div>
                                                    <div><strong>Last time it was encountered:</strong> {errorToReview?.timestamp}</div>
                                                    <div id="urls">
                                                        <strong>URL where the error is found:</strong><br />
                                                        {errorToReview?.url}
                                                    </div>
                                                </div>

                                                <div className="pt-5">{issueContent}</div>
                                            </div>

                                            <div className="text-area col-6 equal-height-div">
                                                <div className="text-area-block" style={{ marginBottom: 0 }}>
                                                    <div className="text-area-title">
                                                        <h5><strong>{panelTitle}</strong></h5>
                                                    </div>
                                                    <div className="text-area-clipboard">
                                                        <Dropdown
                                                            menu={{
                                                                items: [
                                                                    {
                                                                        key: 'copy',
                                                                        label: 'Copy',
                                                                        icon: <Clipboard size={14} />,
                                                                        onClick: () => handleCopyClick(downloadPayload),
                                                                    },
                                                                    {
                                                                        type: 'divider',
                                                                    },
                                                                    {
                                                                        key: 'downloads',
                                                                        label: 'Download',
                                                                        icon: <Download size={14} style={{ marginTop: '5px'}}/>,
                                                                        children: [
                                                                            {
                                                                                key: 'pdf',
                                                                                label: 'Download PDF',
                                                                                icon: <Download size={14} />,
                                                                                onClick: () => downloadAsPDF(
                                                                                    downloadPayload,
                                                                                    `${filenameCategoryPrefix}-${errorToReview?.event_name || 'event'}-${dayjs().format('YYYY-MM-DD')}`
                                                                                ),
                                                                            },
                                                                            {
                                                                                key: 'xls',
                                                                                label: 'Download Excel',
                                                                                icon: <Download size={14} />,
                                                                                onClick: () => downloadAsXLSX(
                                                                                    downloadPayload,
                                                                                    `${filenameCategoryPrefix}-${errorToReview?.event_name || 'event'}-${dayjs().format('YYYY-MM-DD')}`
                                                                                ),
                                                                            },
                                                                        ],
                                                                    },
                                                                ],
                                                            }}
                                                            trigger={['click']}
                                                            placement="bottomRight"
                                                            mouseLeaveDelay={0.1}
                                                            destroyPopupOnHide
                                                        >
                                                            <div className="text-area-copy-button">
                                                                <ThreeDots size={18} />
                                                            </div>
                                                        </Dropdown>
                                                        {panelBody}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </Space>
                        <div className={`charts-area ${tabsVisibility["overview"] ? "visible" : ""}`}>
                            <div>
                                <AntdTable
                                    columns={snapshotsListColumns}
                                    dataSource={keyedSnapshotsList}
                                    rowKey="__rowKey"
                                    pagination={{
                                        showSizeChanger: true,
                                        pageSizeOptions: ['10', '25', '50', '100'],
                                        defaultPageSize: 10,
                                    }}
                                    onRow={(record) => ({
                                        onClick: () => handleAreaUpdate(record, 'snapshot'),
                                    })}
                                />

                                <div className={`mb-3 text-area ${snapshotExpanded ? "slide-down" : "slide-up"}`}>
                                    <div className="text-area-block">
                                        <div className="text-area-title">
                                            <strong>URL:</strong> {snapshotToReview?.hostname || ""}
                                        </div>
                                        <div className="text-area-clipboard">
                                            <Dropdown
                                                menu={{
                                                    items: [
                                                        {
                                                            key: 'copy',
                                                            label: 'Copy',
                                                            icon: <Clipboard size={14} />,
                                                            onClick: () => handleCopyClick(snapshotToReview?.event),
                                                        },
                                                        {
                                                            type: 'divider',
                                                        },
                                                        {
                                                            key: 'downloads',
                                                            label: 'Download',
                                                            icon: <Download size={14} />,
                                                            children: [
                                                                {
                                                                    key: 'pdf',
                                                                    label: 'Download PDF',
                                                                    onClick: () => downloadAsPDF(
                                                                        snapshotToReview?.event || {},
                                                                        `datalayer-snapshot-${snapshotToReview?.page_type || 'event'}-${dayjs().format('YYYY-MM-DD')}`
                                                                    ),
                                                                },
                                                                {
                                                                    key: 'xls',
                                                                    label: 'Download Excel',
                                                                    onClick: () => downloadAsXLSX(
                                                                        snapshotToReview?.event || {},
                                                                        `datalayer-snapshot-${snapshotToReview?.page_type || 'event'}-${dayjs().format('YYYY-MM-DD')}`
                                                                    ),
                                                                },
                                                            ],
                                                        },
                                                    ],
                                                }}
                                                trigger={['click']}
                                                placement="bottomRight"
                                                mouseLeaveDelay={0.1}
                                                destroyPopupOnHide
                                            >
                                                <div className="text-area-copy-button">
                                                    <ThreeDots size={18} />
                                                </div>
                                            </Dropdown>
                                            <textarea style={{ minHeight: '25rem' }} className="datalayer-string-snp ph-no-capture" disabled={true}
                                                value={snapshotText} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Space>
            ) : (
                <div className="col-12 col-sm-10 col-md-8 text-start w-100">
                    <h1>Dataset not found!</h1>
                </div>
            )}
        </Content>
    );
}
